import React from 'react';
import { XIcon } from 'lucide-react';
interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyPolicyModal({
  isOpen,
  onClose
}: PrivacyPolicyModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-id="element-260" data-mp="e2a4d0cqo29o">
      <div className="bg-slate border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl relative" onClick={e => e.stopPropagation()} data-id="element-261" data-mp="16pefln2fdctw">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-slate/95 backdrop-blur z-10 rounded-t-2xl" data-id="element-262" data-mp="x039886xam9p">
          <h2 className="text-2xl font-bold text-white" data-id="element-263" data-mp="11wbhr2qm7h7w">Privacy Policy</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 text-gray-400 hover:text-white transition-colors" aria-label="Close modal" data-id="element-264" data-mp="1pn77lubig2ci">
            <XIcon className="w-6 h-6" data-id="element-265" data-mp="32oyuwl3ypyvg" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 md:p-8 overflow-y-auto text-gray-300 space-y-8 text-sm md:text-base leading-relaxed" data-id="element-266" data-mp="2xr763zym6svh">
          <section data-id="element-267" data-mp="3cgcz366uzmux">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-268" data-mp="37ntp0j7dr9uv">
              Welcome to FIQIS
            </h3>
            <p className="mb-4" data-id="element-269" data-mp="3lef7w7wu3jsr">
              We take your privacy very seriously. Please read this privacy
              policy carefully as it contains important information on who we
              are and our information practices, meaning how and why we collect,
              use, disclose, sell, share, store, and retain your personal
              information. It also explains your rights in relation to your
              personal information and how to contact us or supervisory
              authorities in the event you have a complaint or request.
            </p>
            <p className="mb-4" data-id="element-270" data-mp="2lo5usa0u2t5r">
              We collect your data as part of the service. By using our service
              and purchasing our product, you agree to the collection and use of
              information in accordance with this policy. Unless otherwise
              defined in this Privacy Policy, terms used in this Privacy Policy
              have the same meanings as in our Terms and Conditions, accessible
              from [T&C URL].
            </p>
            <p data-id="element-271" data-mp="34kxl33esm8jb">
              We collect, use, and are responsible for certain personal
              information about you. For California consumers, we are subject to
              the California Consumer Privacy Act of 2018 (CCPA), as amended by
              the California Privacy Rights Act of 2020 (CPRA). We are
              responsible as a "controller" of that personal information for the
              purposes of the GDPR. We are responsible for your personal
              information as a "business" under the CCPA/CPRA.
            </p>
          </section>

          <section data-id="element-272" data-mp="1gq0rs6tn2khf">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-273" data-mp="1gqkd7xihzr07">
              1. Key Terms
            </h3>
            <p className="mb-4" data-id="element-274" data-mp="1bs8zhbp30wcp">
              It would be helpful to start by explaining some key terms used in
              this policy:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-navy/50 p-4 rounded-lg border border-white/5" data-id="element-275" data-mp="11t1ouw9qffta">
              <div className="font-semibold text-teal" data-id="element-276" data-mp="1zf7h4coxuac7">We, us, our</div>
              <div className="md:col-span-2" data-id="element-277" data-mp="1uge0wci9o58c">FIQIS Inc.</div>

              <div className="font-semibold text-teal" data-id="element-278" data-mp="1ppxngseg4jlz">
                Data protection officer contact
              </div>
              <div className="md:col-span-2" data-id="element-279" data-mp="1kre1wt3rl9u5">[email address]</div>

              <div className="font-semibold text-teal" data-id="element-280" data-mp="1kp05ogxclrl0">
                Personal information
              </div>
              <div className="md:col-span-2" data-id="element-281" data-mp="2dfi8rhy2nsyf">
                Any information relating to an identified or identifiable
                individual.
              </div>

              <div className="font-semibold text-teal" data-id="element-282" data-mp="1xw76bgfntl4c">
                Special category personal information
              </div>
              <div className="md:col-span-2" data-id="element-283" data-mp="1nzcbp8mic4vc">
                Personal information revealing racial or ethnic origin,
                political opinions, religious beliefs, philosophical beliefs, or
                trade union membership; genetic and biometric data; and data
                concerning health, sex life or sexual orientation.
              </div>

              <div className="font-semibold text-teal" data-id="element-284" data-mp="2lmb8rkh43etr">
                Sensitive Personal Information
              </div>
              <div className="md:col-span-2" data-id="element-285" data-mp="26wcbdjxds7fc">
                Personal information revealing a consumer's social security
                number, driver's license and passport numbers, account numbers
                and credentials, precise geolocation, racial or ethnic origin,
                religious beliefs, or union membership, personal information
                concerning a consumer's health, sex life, or sexual orientation,
                contents of a consumer's mail, email and text messages where the
                business is not the intended recipient, genetic data, biometric
                information, or citizenship and immigration status.
              </div>

              <div className="font-semibold text-teal" data-id="element-286" data-mp="xrerjm98fh88">
                Biometric Information
              </div>
              <div className="md:col-span-2" data-id="element-287" data-mp="50mpi85hjycz">
                An individual's physiological, biological, or behavioral
                characteristics, including information pertaining to an
                individual's deoxyribonucleic acid (DNA), that is used or is
                intended to be used singly or in combination with each other or
                with other identifying data, to establish individual identity.
                Biometric information includes, but is not limited to, imagery
                of the iris, retina, fingerprint, face, hand, palm, vein
                patterns, and voice recordings, from which an identifier
                template, such as a faceprint, a minutiae template, or a
                voiceprint, can be extracted, and keystroke patterns or rhythms,
                gait patterns or rhythms, and sleep, health, or exercise data
                that contain identifying information.
              </div>
            </div>
          </section>

          <section data-id="element-288" data-mp="4ar9t843kjos">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-289" data-mp="9bf88euno2t1">
              2. Personal Information We Collect About You
            </h3>
            <p className="mb-4" data-id="element-290" data-mp="dbpgxz9u17bl">
              We may collect and use the following personal information,
              including sensitive personal information, that identifies, relates
              to, describes, is reasonable capable of being associated with, or
              could reasonably be linked, directly or indirectly, with a
              particular consumer or household:
            </p>

            <div className="overflow-x-auto" data-id="element-291" data-mp="3e919x3zgd3go">
              <table className="w-full text-left border-collapse" data-id="element-292" data-mp="39apzar1c2oxe">
                <thead data-id="element-293" data-mp="398dfs9fxg6i8">
                  <tr className="border-b border-white/10 text-white" data-id="element-294" data-mp="358mj5p3pyw5s">
                    <th className="py-3 pr-4 font-semibold" data-id="element-295" data-mp="35az9oqnjmeug">Categories</th>
                    <th className="py-3 px-4 font-semibold" data-id="element-296" data-mp="3099ynb0bm16d">Examples</th>
                    <th className="py-3 pl-4 font-semibold whitespace-nowrap" data-id="element-297" data-mp="2vayn8vudnim1">
                      Collected in Last 12 Months?
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5" data-id="element-298" data-mp="2lk43pz8imx4a">
                  <tr data-id="element-299" data-mp="2lij9kaq45g6y">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-300" data-mp="2hjtw6i52rynb">
                      Identifiers
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-301" data-mp="2cjo10rzlju0h">
                      A real name, alias, postal address, unique personal
                      identifier, online identifier, Internet Protocol address,
                      email address, account name, social security number,
                      driver's license number, passport number, or other similar
                      identifiers.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-302" data-mp="3mjgpy8v3s7vp">Yes</td>
                  </tr>
                  <tr data-id="element-303" data-mp="3redyzfsduh8l">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-304" data-mp="3crayr1coduhk">
                      Personal information described in the California Customer
                      Records Statute
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-305" data-mp="33r62nncxmiip">
                      A signature, social security number, physical
                      characteristics or description, address, telephone number,
                      passport number, driver's license or state identification
                      card number, insurance policy number, education,
                      employment, employment history, bank account number,
                      credit card number, debit card number, or any other
                      financial information, medical information, or health
                      insurance information.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-306" data-mp="2p58ng06tugna">Yes</td>
                  </tr>
                  <tr data-id="element-307" data-mp="2k39nl0aaq1v3">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-308" data-mp="2k7789qciv1h3">
                      Characteristics of protected classifications under
                      California or federal law
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-309" data-mp="37u5kucxitp9q">
                      Age (40 years or older), race, color, ancestry, national
                      origin, citizenship, religion or creed, marital status,
                      medical condition, physical or mental disability, sex
                      (including gender, gender identity, gender expression,
                      pregnancy or childbirth and related medical conditions),
                      sexual orientation, veteran or military status, genetic
                      information (including familial genetic information).
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-310" data-mp="29fg8nl0aj9m5">No</td>
                  </tr>
                  <tr data-id="element-311" data-mp="24ftrj0hcd982">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-312" data-mp="24gmrnilsp0h8">
                      Commercial information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-313" data-mp="2x3zfrx7ltgcn">
                      Records of personal property, products or services
                      purchased, obtained, or considered, or other purchasing or
                      consuming histories or tendencies.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-314" data-mp="2o5nduz1aew4w">No</td>
                  </tr>
                  <tr data-id="element-315" data-mp="1gnmq3mwrec1s">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-316" data-mp="1co5nr2cmpv0u">
                      Biometric information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-317" data-mp="3ocshlqbw4ank">
                      Physiological, biological, or behavioral characteristics,
                      DNA information. Imagery of the iris, retina, fingerprint,
                      face, hand, palm, vein patterns, or voice recordings.
                      Keystroke patterns or rhythms, gait patterns or rhythms,
                      or sleep, health, or exercise data.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-318" data-mp="fvfnxtr7948y">No</td>
                  </tr>
                  <tr data-id="element-319" data-mp="jx9kpqy9xo3u">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-320" data-mp="jwrhgad729mf">
                      Internet and network activity
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-321" data-mp="ou9qe0r7oouf">
                      Browsing history, search history, and information
                      regarding a consumer's interaction with an internet
                      website application, or advertisement.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-322" data-mp="3tywmyxp3r0x6">Yes</td>
                  </tr>
                  <tr data-id="element-323" data-mp="3p0v0e2sgwjgy">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-324" data-mp="3p1mpqvvh1kys">
                      Geolocation data
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-325" data-mp="1uc5ydtajesc">
                      Information about physical locations or movements.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-326" data-mp="gq07fv785nsj">Yes</td>
                  </tr>
                  <tr data-id="element-327" data-mp="bsrohse95fq8">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-328" data-mp="br742vlnjd63">
                      Sensory information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-329" data-mp="ljw32r00mykj">
                      Audio, electronic, visual, thermal, olfactory, or similar
                      information.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-330" data-mp="ukkinq5256zj">No</td>
                  </tr>
                  <tr data-id="element-331" data-mp="1e4ru7r8mifi5">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-332" data-mp="1e6mer4fzg5lp">
                      Professional or employment-related information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-333" data-mp="147n3de3u1vv0">
                      Current or past job history or performance evaluations.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-334" data-mp="198b2yo64nl0u">No</td>
                  </tr>
                  <tr data-id="element-335" data-mp="vdig14nwr2k8">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-336" data-mp="vcz17wtk0uaf">
                      Non-public education information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-337" data-mp="lhyqczzxt03x">
                      Non-public education records that are directly related to
                      a student which are maintained by an educational agency or
                      institution or by a party acting on its behalf.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-338" data-mp="ggreaoqrherg">No</td>
                  </tr>
                  <tr data-id="element-339" data-mp="9d1gapxfcm88">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-340" data-mp="9gffjpaskfbg">
                      Inferences drawn from other Personal Data
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-341" data-mp="3vsmepgle7y8k">
                      Information used to create a user profile which reflects
                      the users' preferences, characteristics, psychological
                      trends, predispositions, behavior, attitudes,
                      intelligence, abilities, or aptitudes.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-342" data-mp="3cujk8ma2t93d">No</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 italic" data-id="element-343" data-mp="37xjeydl1wadb">
              If you do not provide personal information required to provide
              service and/or product to you, it may delay or prevent us from
              providing services and/or product to you.
            </p>
          </section>

          <section data-id="element-344" data-mp="1ekuk0se8hrx9">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-345" data-mp="1eldrff69cxx1">
              3. How Your Personal Information is Collected
            </h3>
            <p className="mb-4" data-id="element-346" data-mp="1ndfn705dtq0z">
              We collect personal information from the following categories of
              sources:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-347" data-mp="1sc91gushtl27">
              <li data-id="element-348" data-mp="vn9vxnjamxdp">
                You, directly in person, by telephone, text, or email and/or via
                our website
              </li>
              <li data-id="element-349" data-mp="zomz1pv04mgx">Third party with your consent (e.g., your bank)</li>
              <li data-id="element-350" data-mp="14metaqj4ckoa">Advertising networks</li>
              <li data-id="element-351" data-mp="14psuhvg1qnu5">Internet service providers</li>
              <li data-id="element-352" data-mp="19nkjohpb3vun">Data analytics providers</li>
              <li data-id="element-353" data-mp="19k6gx8cqdkmo">Government entities</li>
              <li data-id="element-354" data-mp="2g3ehb9yfodci">Operating systems and platforms</li>
              <li data-id="element-355" data-mp="2g4pnfgfcpx6s">Social networks</li>
              <li data-id="element-356" data-mp="2l16her98f5rs">Data brokers</li>
              <li data-id="element-357" data-mp="2l04unpiusuu8">Publicly accessible sources (e.g., property records)</li>
              <li data-id="element-358" data-mp="1vcrac1qeha67">Cookies on our website</li>
              <li data-id="element-359" data-mp="1vfn9ftdzi4w8">
                Our IT and security systems, including automated monitoring of
                our websites and other technical systems, such as our computer
                networks and connections, CCTV and access control systems,
                communications systems, email, and instant messaging systems.
              </li>
            </ul>
          </section>

          <section data-id="element-360" data-mp="1cncd6idu5573">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-361" data-mp="1ckq5miqy2pov">
              4. How and Why We Use Your Personal Information
            </h3>
            <p className="mb-4" data-id="element-362" data-mp="1mdxkqvbhlk25">
              Under data protection laws, we can only use your personal
              information if we have a proper reason for doing so, for example:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-363" data-mp="tvjk6nyrnjzd">
              <li data-id="element-364" data-mp="oxi2a6d2tqph">To comply with our legal and regulatory obligations</li>
              <li data-id="element-365" data-mp="ox8cojvy1lkb">
                For the performance of our contract with you or to take steps at
                your request before entering into a contract
              </li>
              <li data-id="element-366" data-mp="35cbw1qcavd9r">
                For our legitimate interests or those of a third party –or–
              </li>
              <li data-id="element-367" data-mp="2qoh3qb27fax7">Where you have given consent</li>
            </ul>
            <p className="mb-4" data-id="element-368" data-mp="2vmsfwsfz1xil">
              A legitimate interest is when we have a business or commercial
              reason to use your information, so long as this is not overridden
              by your own rights and interests.
            </p>
            <p className="mb-4" data-id="element-369" data-mp="3o821xoncstsx">
              We collect the categories of Personal Data listed above for the
              following business purposes:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-370" data-mp="3eanozz0n2n5d">
              <li data-id="element-371" data-mp="1yuyjpbx60ss8">
                To take steps at your request before entering into a contract
                for services and/or products
              </li>
              <li data-id="element-372" data-mp="22uyzazw03a0v">
                To provide the services and/or products to you for their
                intended purposes.
              </li>
              <li data-id="element-373" data-mp="2pla830rkk36s">
                For our legitimate interests or those of a third party, i.e., to
                minimize fraud that could be damaging for us and for you.
              </li>
              <li data-id="element-374" data-mp="2gleqoe7f7byb">
                To send communications to you, including marketing
                communications (unless you opt-out).
              </li>
              <li data-id="element-375" data-mp="3dd8k5myue2kz">
                To accomplish the purposes outlined in our Terms of Service.
              </li>
              <li data-id="element-376" data-mp="34iuabzx2zk99">
                To process your payments if you make a purchase through the
                Services, including sharing your payment information with third
                party payment processors. We will not store or collect your
                payment card details. That information is provided directly to
                our third-party payment processors whose use of your personal
                information is governed by their own Privacy Policy. These
                payment processors adhere to the standards set by PCI-DSS as
                managed by the PCI Security Standards Council.
              </li>
              <li data-id="element-377" data-mp="dba1swluj0ux">To comply with our legal and regulatory obligations.</li>
            </ul>
          </section>

          <section data-id="element-378" data-mp="hepx9pm6iv61">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-379" data-mp="hf9e1303fdih">
              5. Who We Share Your Personal Information With
            </h3>
            <p className="mb-4" data-id="element-380" data-mp="rbkt20r2ihgm">
              We routinely share personal information with:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-381" data-mp="w2ksbcnntbma">
              <li data-id="element-382" data-mp="w5709degpt51">
                Our affiliates, including companies within the FiQIS group
              </li>
              <li data-id="element-383" data-mp="153sp1qaj24e9">
                Service providers we use to help deliver our products and/or
                services to you, such as payment service providers, warehouses,
                and delivery companies
              </li>
              <li data-id="element-384" data-mp="2b09bibmn6dyz">
                Other third parties we use to help us run our business, such as
                marketing agencies or website hosts
              </li>
              <li data-id="element-385" data-mp="2k05txvifugll">
                Third parties approved by you, including social media sites you
                choose to link your account to or third-party payment providers
              </li>
            </ul>
            <p className="mb-4" data-id="element-386" data-mp="2tyve5uxt4d50">
              We only allow our service providers to handle your personal
              information if we are satisfied they take appropriate measures to
              protect your personal information. We also impose contractual
              obligations on service providers relating to ensure they can only
              use your personal information to provide services to us and to
              you. We may also share personal information with external
              auditors, e.g., in relation to ISO accreditation and the audit of
              our accounts.
            </p>
            <p className="mb-4" data-id="element-387" data-mp="3rsmoxp5yguf1">
              We may disclose and exchange information with law enforcement
              agencies and regulatory bodies to comply with our legal and
              regulatory obligations.
            </p>
            <p data-id="element-388" data-mp="3hvhz9fcwigth">
              We may also need to share some personal information with other
              parties, such as potential buyers of some or all of our business
              or during a re-structuring. We will typically anonymize
              information, but this may not always be possible. The recipient of
              the information will be bound by confidentiality obligations.
            </p>
          </section>

          <section data-id="element-389" data-mp="ltleenowj7c">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-390" data-mp="k8ssukxrac4">
              6. Personal Information We Sold or Shared
            </h3>
            <p className="mb-4" data-id="element-391" data-mp="x5arofoquyxm">
              We will not sell your Personal Information to any third parties.
              Notwithstanding the preceding statement, in the preceding 12
              months, we have shared the following categories of personal
              information:
            </p>
            <div className="overflow-x-auto" data-id="element-392" data-mp="2rl94euw3zqfw">
              <table className="w-full text-left border-collapse" data-id="element-393" data-mp="2cva68s4k0f0f">
                <thead data-id="element-394" data-mp="2htli18eeszla">
                  <tr className="border-b border-white/10 text-white" data-id="element-395" data-mp="2hstnm0wjt7wl">
                    <th className="py-3 pr-4 font-semibold" data-id="element-396" data-mp="23ttyks54iqzw">Categories</th>
                    <th className="py-3 px-4 font-semibold" data-id="element-397" data-mp="23vwx60kcdsg8">
                      Shared in last 12 months?
                    </th>
                    <th className="py-3 pl-4 font-semibold" data-id="element-398" data-mp="1tvwb46v38y8c">Recipients</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5" data-id="element-399" data-mp="1ytxw4xc2r7mg">
                  <tr data-id="element-400" data-mp="3o9ntburhgqfe">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-401" data-mp="3o95q2e6elbxz">
                      Identifiers
                    </td>
                    <td className="py-3 px-4" data-id="element-402" data-mp="3t7gzinssdkfo">Yes</td>
                    <td className="py-3 pl-4" data-id="element-403" data-mp="1hletvm0whm88">Affiliates, Service Providers</td>
                  </tr>
                  <tr data-id="element-404" data-mp="1cmlnseny0xi3">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-405" data-mp="1cn3j992s77oy">
                      Personal information (CA Customer Records Statute)
                    </td>
                    <td className="py-3 px-4" data-id="element-406" data-mp="1mkhzp8opfwh9">Yes</td>
                    <td className="py-3 pl-4" data-id="element-407" data-mp="1mix2tcex41g8">Affiliates, Service Providers</td>
                  </tr>
                  <tr data-id="element-408" data-mp="20abl1h103ym8">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-409" data-mp="20dze6f2jaj0f">
                      Internet and network activity
                    </td>
                    <td className="py-3 px-4" data-id="element-410" data-mp="2aacbdmxafezx">Yes</td>
                    <td className="py-3 pl-4" data-id="element-411" data-mp="2a7q2njynutg4">Affiliates, Service Providers</td>
                  </tr>
                  <tr data-id="element-412" data-mp="2596fjg8bzbm6">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-413" data-mp="fvt9nmkklfiy">
                      Geolocation data
                    </td>
                    <td className="py-3 px-4" data-id="element-414" data-mp="ay9soewhmonb">Yes</td>
                    <td className="py-3 pl-4" data-id="element-415" data-mp="ce5h1prpyf9d">Yes</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section data-id="element-416" data-mp="hbnhssvl1o6a">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-417" data-mp="hfbajpt7e6jy">
              7. Categories of Personal Information We Disclosed for a Business
              Purpose
            </h3>
            <p className="mb-4" data-id="element-418" data-mp="ramqgbgcljta">
              In the preceding 12 months, we have disclosed the following
              categories of personal information for a business purpose:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-419" data-mp="3useejerd5jm7">
              <li data-id="element-420" data-mp="3la8on6yg3fd">
                Identifiers (e.g., a real name, alias, postal address, unique
                personal identifier, online identifier, Internet Protocol
                address, email address, account name, social security number,
                driver's license number, passport number, or other similar
                identifiers)
              </li>
              <li data-id="element-421" data-mp="1t6iqks7wwbk5">
                Information that identifies, relates to, describes, or is
                capable of being associated with, a particular individual,
                including, but not limited to, his or her name, signature,
                social security number, physical characteristics or description,
                address, telephone number, passport number, driver's license or
                state identification card number, insurance policy number,
                education, employment, employment history, bank account number,
                credit card number, debit card number, or any other financial
                information, medical information, or health insurance
                information
              </li>
              <li data-id="element-422" data-mp="whjf74c16tnq">
                Internet or other electronic network activity information (e.g.,
                browsing history, search history, and information regarding a
                consumer's interaction with an Internet Web site, application,
                or advertisement)
              </li>
            </ul>
          </section>

          <section data-id="element-423" data-mp="htjffcnjlquz">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-424" data-mp="hu2oe3v7t4wv">
              8. How Long Your Personal Information Will Be Kept
            </h3>
            <p className="mb-4" data-id="element-425" data-mp="3vl1u9h1pygj">
              We will keep your personal information while you have an account
              with us or while we are providing products and/or services to you.
              Thereafter, we will keep your personal information for as long as
              is necessary:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-426" data-mp="10lvgzx6dgo42">
              <li data-id="element-427" data-mp="mruovm9xdzco">
                To respond to any questions, complaints or claims made by you or
                on your behalf
              </li>
              <li data-id="element-428" data-mp="1ejtul0cvcgii">To show that we treated you fairly –or–</li>
              <li data-id="element-429" data-mp="1ei90tcydp1lp">To keep records required by law</li>
            </ul>
            <p data-id="element-430" data-mp="1jasee9x2fwqi">
              We will not retain your personal information for longer than
              necessary for the purposes set out in this policy. Different
              retention periods apply for different types of personal
              information. When it is no longer necessary to retain your
              personal information, we will delete or anonymize it.
            </p>
          </section>

          <section data-id="element-431" data-mp="38syz7v3sd24l">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-432" data-mp="38sfq93w45o2p">
              9. California Consumers: Your Rights Under the CCPA/CPRA
            </h3>
            <p className="mb-4" data-id="element-433" data-mp="2g3s12x88uejv">
              You have the right under the California Consumer Privacy Act of
              2018 (CCPA), as amended by the California Privacy Rights Act of
              2020 (CPRA), and certain other privacy and data protection laws,
              as applicable, to exercise free of charge:
            </p>

            <h4 className="font-semibold text-teal mt-6 mb-2" data-id="element-434" data-mp="a877gaztiqpl">
              Disclosure of Personal Information We Collect About You
            </h4>
            <p className="mb-2" data-id="element-435" data-mp="1yu67xdzqyqpq">
              You have the right to know, and request disclosure of:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-4" data-id="element-436" data-mp="23tsn3sz4yh16">
              <li data-id="element-437" data-mp="28ru6kf0h4id6">
                The categories of personal information we have collected about
                you, including sensitive personal information
              </li>
              <li data-id="element-438" data-mp="2dknbb2vxzqp9">
                The categories of sources from which the personal information is
                collected
              </li>
              <li data-id="element-439" data-mp="2mih443ui027o">
                Our business or commercial purpose for collecting, selling, or
                sharing personal information
              </li>
              <li data-id="element-440" data-mp="2rgimsno0hzim">
                The categories of third parties to whom we disclose personal
                information, if any –and–
              </li>
              <li data-id="element-441" data-mp="x5m0bng2zeka">
                The specific pieces of personal information we have collected
                about you
              </li>
            </ul>
            <p className="mb-2" data-id="element-442" data-mp="ujmcgyruf7ds">Please note that we are not required to:</p>
            <ul className="list-disc pl-5 space-y-1 mb-6" data-id="element-443" data-mp="ulz4k4rbey4k">
              <li data-id="element-444" data-mp="ps4kh07ssh4y">
                Retain any personal information about you that was collected for
                a single one-time transaction if, in the ordinary course of
                business, that information about you is not retained
              </li>
              <li data-id="element-445" data-mp="1digt6davahwl">
                Reidentify or otherwise link any data that, in the ordinary
                course of business, is not maintained in a manner that would be
                considered personal information –or–
              </li>
              <li data-id="element-446" data-mp="13iznhvvm840m">
                Provide the personal information to you more than twice in a
                12-month period
              </li>
            </ul>

            <h4 className="font-semibold text-teal mt-6 mb-2" data-id="element-447" data-mp="3obbaoar1a9cl">
              Disclosure of Personal Information Sold, Shared, or Disclosed for
              a Business Purpose
            </h4>
            <p className="mb-2" data-id="element-448" data-mp="2hjj3sfhfovs1">
              In connection with any personal information we may sell, share, or
              disclose to a third party for a business purpose, you have the
              right to know:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-4" data-id="element-449" data-mp="2ranhzczacclt">
              <li data-id="element-450" data-mp="2mjf5g82nsb4f">
                The categories of personal information about you that we sold or
                shared and the categories of third parties to whom the personal
                information was sold or shared –and–
              </li>
              <li data-id="element-451" data-mp="2vb7ecec1bc6w">
                The categories of personal information that we disclosed about
                you for a business purpose and the categories of persons to whom
                the personal information was disclosed for a business purpose
              </li>
            </ul>
            <p className="mb-4" data-id="element-452" data-mp="1fv8l0972tkqp">
              You have the right to opt-out of the sale of your personal
              information or sharing of your personal information for the
              purpose of targeted behavioral advertising. If you exercise your
              right to opt-out of the sale or sharing of your personal
              information, we will refrain from selling or sharing your personal
              information, unless you subsequently provide express authorization
              for the sale or sharing of your personal information.
            </p>
            <p className="mb-6 italic text-gray-400" data-id="element-453" data-mp="33rerq87qti79">
              We do not share or sell personal data at any point outside of
              financial institutions. It is up to your financial institution to
              enable the opt out options. This site itself does not collect
              information.
            </p>

            <h4 className="font-semibold text-teal mt-6 mb-2" data-id="element-454" data-mp="2ytn1dimqy456">
              Right to Limit Use of Sensitive Personal Information
            </h4>
            <p className="mb-2" data-id="element-455" data-mp="x2josntaqfhz">
              You have the right to limit the use and disclosure of your
              sensitive personal information to the use which is necessary to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-456" data-mp="ql8frlzsuke2">
              <li data-id="element-457" data-mp="qnuldg34qptn">
                Perform the services or provide the goods reasonably expected by
                an average consumer who requests those goods or services
              </li>
              <li data-id="element-458" data-mp="gpesp8ogf8i1">
                To perform the following services: (1) Helping to ensure
                security and integrity to the extent the use of the consumer's
                personal information is reasonably necessary and proportionate
                for these purposes; (2) Short-term, transient use, including,
                but not limited to, non-personalized advertising shown as part
                of a consumer's current interaction with the business, provided
                that the consumer's personal information is not disclosed to
                another third party and is not used to build a profile about the
                consumer or otherwise alter the consumer's experience outside
                the current interaction with the business; (3) Performing
                services on behalf of the business, including maintaining or
                servicing accounts, providing customer service, processing or
                fulfilling orders and transactions, verifying customer
                information, processing payments, providing financing, providing
                analytic services, providing storage, or providing similar
                services on behalf of the business; and (4) Undertaking
                activities to verify or maintain the quality or safety of a
                service or device that is owned, manufactured, manufactured for,
                or controlled by the business, and to improve, upgrade, or
                enhance the service or device that is owned, manufactured,
                manufactured for, or controlled by the business –and–
              </li>
              <li data-id="element-459" data-mp="7tno1eas0zvd">As authorized by further regulations</li>
            </ul>
            <p className="mb-4" data-id="element-460" data-mp="2v423dw6no30">
              You have a right to know if your sensitive personal information
              may be used, or disclosed to a service provider or contractor, for
              additional, specified purposes.
            </p>
            <p className="italic text-gray-400" data-id="element-461" data-mp="cm8eq6ydywuo">
              We do not share or sell personal data at any point outside of
              financial institutions. It is up to your financial institution to
              enable the opt out options. This site itself does not collect
              information.
            </p>
          </section>

          <section data-id="element-462" data-mp="3pztobj53m0hi">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-463" data-mp="3pzaih0sq32ju">
              10. How to Exercise Your Rights
            </h3>
            <p className="mb-4" data-id="element-464" data-mp="199v8fx42s14f">
              If you would like to exercise any of your rights as described in
              this Privacy Policy, you can email us at arthur@fiqis.com or mail
              us at: FIQIS Inc.
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-465" data-mp="1cvgj0c7rpmwb">
              <li data-id="element-466" data-mp="1hsyjrfbmsvt8">
                Please note that you may only make a CCPA/CPRA-related data
                access or data portability disclosure request twice within a
                12-month period.
              </li>
              <li data-id="element-467" data-mp="17u9b8d5mknpn">
                If you choose to contact us directly by email/ in writing, you
                will need to provide us with:
                <ul className="list-[circle] pl-5 mt-2 space-y-1" data-id="element-468" data-mp="20j7vpgx801y4">
                  <li data-id="element-469" data-mp="20kizhgqo19p8">
                    Enough information to identify you (e.g., your full name,
                    address and customer or matter reference number)
                  </li>
                  <li data-id="element-470" data-mp="1qqsckga6t7bn">
                    Proof of your identity and address (e.g., a copy of your
                    driving license or passport and a recent utility or credit
                    card bill) –and–
                  </li>
                  <li data-id="element-471" data-mp="2t5odhvp0uhob">
                    A description of what right you want to exercise and the
                    information to which your request relates
                  </li>
                </ul>
              </li>
              <li data-id="element-472" data-mp="2k3f273lxik9v">
                We are not obligated to make a data access or data portability
                disclosure if we cannot verify that the person making the
                request is the person about whom we collected information or is
                someone authorized to act on such person's behalf.
              </li>
              <li data-id="element-473" data-mp="25dhcukc9joj3">
                Any personal information we collect from you to verify your
                identity in connection with you request will be used solely for
                the purposes of verification.
              </li>
            </ul>
          </section>

          <section data-id="element-474" data-mp="1rf9lcrxzxffb">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-475" data-mp="1rhvo8e9xz6r7">
              11. Keeping Your Personal Information Secure
            </h3>
            <p data-id="element-476" data-mp="1zgpqyz9ys0v">
              We have appropriate security measures in place to prevent personal
              information from being accidentally lost or used or accessed in an
              unauthorized way. We limit access to your personal information to
              those who have a genuine business need to access it. Those
              processing your information will do so only in an authorized
              manner and are subject to a duty of confidentiality. We
              continually test our systems and are ISO 27001 certified, which
              means we follow top industry standards for information security.
              We also have procedures in place to deal with any suspected data
              security breach. We will notify you and any applicable regulator
              of a suspected data security breach where we are legally required
              to do so.
            </p>
          </section>

          <section data-id="element-477" data-mp="30n4fg7f3bs4b">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-478" data-mp="30mkvkl5vqtnn">
              12. Changes to This Privacy Notice
            </h3>
            <p className="mb-4" data-id="element-479" data-mp="1p2ig67wpnjht">
              This privacy notice was published on 03-10-2026 and last updated
              on 03-10-2026.
            </p>
            <p data-id="element-480" data-mp="240uaw90iwi6c">
              We may change this privacy notice from time to time– when we do,
              your continued use of the Services after the amended Privacy
              Policy is posted on the Services will constitute your
              acknowledgment and agreement to the amended Privacy Policy.
              However, to the extent the amended Privacy Policy materially
              alters your rights or obligations in this Privacy Policy, the
              amended Privacy Policy will become effective upon the earlier of
              (a) your continued use of the Services with actual knowledge of
              the amended Privacy Policy, or (b) 30 days after the amended
              Privacy Policy is posted on the Services.
            </p>
          </section>

          <section data-id="element-481" data-mp="3adxe5e92qrao">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-482" data-mp="3abb6lem6obsg">
              13. How to Contact Us
            </h3>
            <p className="mb-4" data-id="element-483" data-mp="31cptw03e7qzl">
              If you have any questions comments, or complaints about this
              Privacy Policy, how we collect and use your information, or your
              choices and rights regarding the same, then you may contact us at
              the contact information below. If you are contacting us as an
              authorized agent for another person or business, please also
              provide sufficient information in your request so that we can
              verify that you are authorized to communicate with us as that
              person's or business' authorized agent.
            </p>
            <div className="bg-navy/50 p-4 rounded-lg border border-white/5 space-y-2" data-id="element-484" data-mp="28p3ii5nxbi26">
              <p data-id="element-485" data-mp="2dnescge7xske">
                <span className="font-semibold text-teal" data-id="element-486" data-mp="2dltxepnzs7m0">Phone:</span>{' '}
                480-462-9577
              </p>
              <p data-id="element-487" data-mp="34qgo9xay8q3h">
                <span className="font-semibold text-teal" data-id="element-488" data-mp="39ngty73vzqu2">Email:</span>{' '}
                arthur@fiqis.com
              </p>
              <p data-id="element-489" data-mp="2uz2emwpaa9wm">
                <span className="font-semibold text-teal" data-id="element-490" data-mp="2uxixl3vszuts">Address:</span>{' '}
                fiqis.com
              </p>
            </div>
          </section>

          <section data-id="element-491" data-mp="2lxvywpugl34f">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-492" data-mp="2lxcodu751h0f">
              14. Do You Need Extra Help?
            </h3>
            <p data-id="element-493" data-mp="2q091x6htmmxi">
              If you would like this notice in another format (for example:
              audio, large print, braille) please contact us (see "How to
              contact us" above).
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 bg-slate/95 backdrop-blur rounded-b-2xl flex justify-end" data-id="element-494" data-mp="a7tkavar0h37">
          <button onClick={onClose} className="bg-teal hover:bg-teal-400 text-navy font-semibold px-6 py-2.5 rounded-md transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)]" data-id="element-495" data-mp="f6d9d4kn28zs">
            Close
          </button>
        </div>
      </div>
    </div>;
}